import React from 'react';
import {
  string, oneOf, number
} from 'prop-types';
import moment from 'moment/moment';
import { getTranslation, langType, withLang } from '../../../i18n';
import { publicationStatusValues, SurveySchemaEditorType } from '../../../constants/types';
import { DISPLAY_DATE_FORMAT } from '../../../constants';

import styles from './infoBox.module.css';

const getStatusIconClassName = (status, isLockedFromEditing) => {
  switch (status) {
    case publicationStatusValues.ARCHIVED:
      return 'icon--archive';
    case publicationStatusValues.PUBLISHED:
      return isLockedFromEditing ? 'icon--permission-private' : 'icon--edit-event';
    default:
      return 'icon--edit-event';
  }
};

const StatusRowComponent = ({
  translate: t, status, useCount, dataCy
}) => {
  const isLockedFromEditing = useCount > 0;
  const statusText = isLockedFromEditing && status === publicationStatusValues.PUBLISHED
    ? t('status.IN_USE') : t(`status.${status}`);
  const statusIconClassName = getStatusIconClassName(status, isLockedFromEditing);

  return (
    <div className={styles.row} data-cy={dataCy}>
      <div className={styles.label}><p>{t('common.status')}</p></div>
      <div className={styles.text}>
        <p>
          <span className={`${styles.statusIcon} ${statusIconClassName}`} />
          {statusText}
        </p>
      </div>
    </div>
  );
};

StatusRowComponent.propTypes = {
  status: oneOf(Object.values(publicationStatusValues)).isRequired,
  useCount: number.isRequired,
  dataCy: string,
  ...langType
};

StatusRowComponent.defaultProps = {
  dataCy: null
};

const StatusRow = withLang(StatusRowComponent);

const InfoRow = ({ label, text, dataCy }) => (
  <div className={styles.row} data-cy={dataCy}>
    <div className={styles.label}>{label}</div>
    <div className={styles.info}>{text}</div>
  </div>
);

InfoRow.propTypes = {
  label: string.isRequired,
  text: string,
  dataCy: string
};

InfoRow.defaultProps = {
  text: null,
  dataCy: null
};

const SurveyInfoBox = ({ translate: t, lang, surveySchema }) => {
  const
    {
      title, code, type, status, description, lastModifiedDate, lastModifiedBy, useCount, version
    }
    = surveySchema;
  const modifiedDate = moment(lastModifiedDate).format(DISPLAY_DATE_FORMAT);

  return (
    <div className={styles.infoBox}>
      <InfoRow label={t('common.name')} text={getTranslation(title, lang)} dataCy="name" />
      <InfoRow label={t('surveyParts.identifier')} text={code} dataCy="identifier" />
      <InfoRow label={t('common.version')} text={version.toString()} dataCy="version" />
      <InfoRow label={t('common.type')} text={t(`surveyType.${type}`)} dataCy="type" />
      <StatusRow translate={t} status={status} useCount={useCount} dataCy="status" />
      <InfoRow label={t('common.modifiedAt')} text={`${modifiedDate} ${lastModifiedBy}`} dataCy="modifiedAt" />
      <InfoRow label={t('common.description')} text={getTranslation(description, lang)} dataCy="description" />
    </div>
  );
};

SurveyInfoBox.propTypes = {
  surveySchema: SurveySchemaEditorType.isRequired,
  ...langType
};

export default withLang(SurveyInfoBox);
